import { apmBase } from '@elastic/apm-rum';
import type { AxiosError, AxiosInstance, CreateAxiosDefaults } from 'axios';
import axios from 'axios';

// 401 is a session flow (logout/redirect) and 429 is throttling; neither is an app failure worth an APM error.
const UNREPORTED_STATUSES = [401, 429];

const MAX_BODY_LENGTH = 100;

function toReportedBody(data: unknown): string {
    if (typeof data === 'string') return data.slice(0, MAX_BODY_LENGTH);

    try {
        return (JSON.stringify(data) ?? '').slice(0, MAX_BODY_LENGTH);
    } catch {
        // e.g. a BigInt or a circular object from a custom transformResponse; keep the rest of the report.
        return '[unserializable]';
    }
}

function reportHttpError(instance: AxiosInstance, { config, response }: AxiosError) {
    if (!config || !response) return;
    if (UNREPORTED_STATUSES.includes(response.status)) return;

    const requestUrl = instance.getUri(config);
    const method = config.method?.toUpperCase() ?? 'UNKNOWN';
    const { pathname } = new URL(requestUrl);
    const report = Object.assign(new Error(`HTTP ${response.status} ${method} ${pathname}`), {
        requestUrl,
        // The body as the browser sent it, to tell a malformed request from a server that rejected a valid one.
        requestBody: toReportedBody(config.data),
        responseBody: toReportedBody(response.data),
    });
    // APM groups errors by type plus stack frames, which are the same for every HTTP failure; a per-endpoint type splits the groups.
    report.name = report.message;

    apmBase.captureError(report);
}

export function createApiInstance(config: CreateAxiosDefaults): AxiosInstance {
    const instance = axios.create(config);

    instance.interceptors.response.use(undefined, (error: unknown) => {
        if (axios.isAxiosError(error)) {
            try {
                reportHttpError(instance, error);
            } catch (reportError) {
                // Reporting must never change what the caller receives.
                console.warn('Failed to report an HTTP error to APM', reportError);
            }
        }
        throw error;
    });

    return instance;
}
